import React, { useState, useEffect, useCallback } from 'react';
import {
  PlusCircle, Trash2, Edit2, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, Loader2, X, Youtube, 
  Image as ImageIcon, Upload, User, Calendar
} from 'lucide-react';
import marathiService from '../../services/marathiService';

// Custom Alert Component
const Alert = ({ children, variant, className = '' }) => {
  const baseStyles = 'flex w-full rounded-lg border p-4';
  const variantStyles = variant === 'destructive'
    ? 'border-red-200 bg-red-50 text-red-600'
    : 'border-gray-200 bg-gray-50 text-gray-600';

  return (
    <div className={`${baseStyles} ${variantStyles} ${className}`}>
      {children}
    </div>
  );
};

const AlertDescription = ({ children, className = '' }) => (
  <div className={`text-sm ${className}`}>{children}</div>
);

const MarathiInfomatics = () => {
  const [infomatics, setInfomatics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedInfomatic, setSelectedInfomatic] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    author: 'Admin',
    videoUrl: '',
    thumbnail: '',
    images: [],
    description: [''],
    date: new Date().toISOString().split('T')[0]
  });

  const fetchInfomatics = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await marathiService.infomatics.getAll({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
      });
      
      if (Array.isArray(response)) {
        setInfomatics(response);
        setTotalPages(Math.ceil(response.length / itemsPerPage) || 1);
      } else if (response && response.data) {
        setInfomatics(response.data);
        setTotalPages(response.totalPages || Math.ceil(response.data.length / itemsPerPage) || 1);
      } else {
        console.error('Unexpected response format:', response);
        setInfomatics([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error fetching infomatics:', err);
      setError(err.message || 'Failed to fetch infomatics');
      setInfomatics([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm]);

  useEffect(() => {
    fetchInfomatics();
  }, [fetchInfomatics]);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await marathiService.infomatics.delete(selectedInfomatic._id);
      setSuccess('Infomatic deleted successfully!');
      setShowDeleteModal(false);
      fetchInfomatics();
    } catch (err) {
      console.error('Error deleting infomatic:', err);
      setError(err.message || 'Failed to delete infomatic');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      
      if (!formData.title.trim()) {
        setError('Title is required');
        setLoading(false);
        return;
      }
      
      if (!formData.thumbnail.trim()) {
        setError('Thumbnail is required');
        setLoading(false);
        return;
      }
      
      if (!formData.description.some(item => item.trim())) {
        setError('At least one description paragraph is required');
        setLoading(false);
        return;
      }
      
      const dataToSend = {
        ...formData,
        description: formData.description.filter(desc => desc.trim())
      };
      
      if (selectedInfomatic) {
        await marathiService.infomatics.update(selectedInfomatic._id, dataToSend);
        setSuccess('Infomatic updated successfully!');
      } else {
        await marathiService.infomatics.create(dataToSend);
        setSuccess('Infomatic created successfully!');
      }
      setShowFormModal(false);
      fetchInfomatics();
    } catch (err) {
      console.error('Error saving infomatic:', err);
      setError(err.message || 'Failed to save infomatic');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDescriptionChange = (index, value) => {
    const newDescription = [...formData.description];
    newDescription[index] = value;
    setFormData({ ...formData, description: newDescription });
  };

  const addDescriptionItem = () => {
    setFormData({ ...formData, description: [...formData.description, ''] });
  };

  const removeDescriptionItem = (index) => {
    const newDescription = formData.description.filter((_, i) => i !== index);
    setFormData({ ...formData, description: newDescription });
  };

  const handleFileChange = (e, type) => {
    if (type === "thumbnail" && typeof e === "string") {
      setFormData(prev => ({ ...prev, thumbnail: e }));
      return;
    }

    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError(`${type === "thumbnail" ? "Thumbnail" : "Image"} size should be less than 5MB`);
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === "thumbnail") {
        setFormData(prev => ({ ...prev, thumbnail: reader.result }));
      } else if (type === "image") {
        if (formData.images.length >= 5) {
          setError("Maximum 5 images are allowed");
          return;
        }
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, reader.result]
        }));
      }
    };
    reader.readAsDataURL(file);
  };

  const openEditModal = (infomatic) => {
    setSelectedInfomatic(infomatic);
    setFormData({
      title: infomatic.title || '',
      author: infomatic.author || 'Admin',
      videoUrl: infomatic.videoUrl || '',
      thumbnail: infomatic.thumbnail || '',
      images: infomatic.images || [],
      description: infomatic.description?.length ? [...infomatic.description] : [''],
      date: infomatic.date ? new Date(infomatic.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
    });
    setShowFormModal(true);
  };

  const openCreateModal = () => {
    setSelectedInfomatic(null);
    setFormData({
      title: '',
      author: 'Admin',
      videoUrl: '',
      thumbnail: '',
      images: [],
      description: [''],
      date: new Date().toISOString().split('T')[0]
    });
    setShowFormModal(true);
  };

  return (
    <div className="min-h-screen bg-white p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-black">Infomatics Management</h1>
        <button
          onClick={openCreateModal}
          className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <PlusCircle className="mr-2" size={20} />
          Add New Infomatic
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {success && (
        <Alert className="mb-4 bg-green-50 text-green-700 border-green-200">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="animate-spin text-red-600" size={32} />
        </div>
      )}

      {/* Infomatics Card Grid */}
      {!loading && !error && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {infomatics.length > 0 ? (
              infomatics.map((infomatic) => (
                <div key={infomatic._id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow">
                  {/* Thumbnail Section */}
                  {infomatic.thumbnail && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={infomatic.thumbnail}
                        alt={infomatic.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Content Section */}
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-xl font-semibold text-gray-800">{infomatic.title}</h3>
                      {infomatic.videoUrl && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full bg-red-100 text-red-800 text-xs">
                          <Youtube className="mr-1" size={14} />
                          Video
                        </span>
                      )}
                    </div>
                    
                    {/* Infomatic Details */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-gray-600">
                        <User size={16} className="mr-2" />
                        <span className="text-sm">{infomatic.author || 'Admin'}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Calendar size={16} className="mr-2" />
                        <span className="text-sm">
                          {infomatic.date ? new Date(infomatic.date).toLocaleDateString() : '-'}
                        </span>
                      </div>
                    </div>

                    {/* Description Preview */}
                    <div className="mb-4">
                      <p className="text-gray-600 line-clamp-3">
                        {infomatic.description?.[0] || 'No description'}
                      </p>
                    </div>

                    {/* Additional Images Indicator */}
                    {infomatic.images && infomatic.images.length > 0 && (
                      <div className="mb-4">
                        <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs">
                          <ImageIcon className="mr-1" size={14} />
                          {infomatic.images.length} image{infomatic.images.length > 1 ? 's' : ''}
                        </span>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => openEditModal(infomatic)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                      >
                        <Edit2 size={20} />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedInfomatic(infomatic);
                          setShowDeleteModal(true);
                        }}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-600">No infomatics found. Create one to get started!</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {infomatics.length > 0 && (
            <div className="flex items-center justify-between border-t border-gray-200 pt-4">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(currentPage * itemsPerPage, infomatics.length)}</span> of{' '}
                    <span className="font-medium">{infomatics.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => goToPage(1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      <span className="sr-only">First</span>
                      <ChevronsLeft size={16} />
                    </button>
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeft size={16} />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => goToPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === page
                            ? 'z-10 bg-red-50 border-red-500 text-red-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRight size={16} />
                    </button>
                    <button
                      onClick={() => goToPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                    >
                      <span className="sr-only">Last</span>
                      <ChevronsRight size={16} />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Infomatic</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this infomatic? This action cannot be undone.</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedInfomatic ? 'Edit Infomatic' : 'Add New Infomatic'}
              </h3>
              <button
                onClick={() => setShowFormModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>

              {/* Author */}
              <div>
                <label htmlFor="author" className="block text-sm font-medium text-gray-700 mb-1">
                  Author *
                </label>
                <input
                  type="text"
                  id="author"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>

              {/* Video URL */}
              <div>
                <label htmlFor="videoUrl" className="block text-sm font-medium text-gray-700 mb-1">
                  Video URL
                </label>
                <input
                  type="text"
                  id="videoUrl"
                  name="videoUrl"
                  value={formData.videoUrl}
                  onChange={handleInputChange}
                  placeholder="Enter any video URL or embed link"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                />
                {formData.videoUrl && (
                  <div className="mt-2 text-xs text-gray-500 flex items-center">
                    <Youtube className="mr-1 text-red-600" size={14} />
                    Video attached
                  </div>
                )}
              </div>

              {/* Thumbnail */}
              <div>
                <label htmlFor="thumbnail" className="block text-sm font-medium text-gray-700 mb-1">
                  Thumbnail *
                </label>
                <div className="flex items-center space-x-4">
                  {formData.thumbnail && (
                    <img
                      src={formData.thumbnail}
                      alt="Thumbnail preview"
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                  )}
                  <div className="flex-1 space-y-2">
                    <label className="flex flex-col items-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-red-500">
                      <ImageIcon className="text-gray-400 mb-2" size={24} />
                      <span className="text-sm text-gray-600">Upload thumbnail</span>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, "thumbnail")}
                        required={!formData.thumbnail}
                      />
                    </label>
                   
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label className="block text-sm font-medium text-gray-700">
                    Description *
                  </label>
                  <button
                    type="button"
                    onClick={addDescriptionItem}
                    className="text-red-500 hover:text-red-600"
                  >
                    + Add Paragraph
                  </button>
                </div>
                {formData.description.map((desc, index) => (
                  <div key={index} className="relative">
                    <textarea
                      value={desc}
                      onChange={(e) => handleDescriptionChange(index, e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                      required
                    />
                    {formData.description.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeDescriptionItem(index)}
                        className="absolute top-2 right-2 text-red-600 hover:text-red-900"
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Date */}
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                  required
                />
              </div>

              {/* Additional Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Images (Max 5)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {formData.images.map((image, index) => (
                    <div key={index} className="relative">
                      <img
                        src={image}
                        alt={`Additional ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            images: prev.images.filter((_, i) => i !== index)
                          }));
                        }}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                  {formData.images.length < 5 && (
                    <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-red-500">
                      <Upload className="text-gray-400 mb-2" size={24} />
                      <span className="text-sm text-gray-600">Add image</span>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, "image")}
                      />
                    </label>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowFormModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  {selectedInfomatic ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarathiInfomatics;